import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { isAxiosError } from 'axios';

import { NewTabsHeader, NEW_TABS_INDEX } from './NewTabsHeader';
import { NewNextButton } from './NewNextButton';
import { type AccessDataValues, type ProfessionalDataValues } from './types';
import { ProfessionalAndPersonalDataSchema } from './schemas/professionalDataSchema';
import { PersonalDataFields, ProfessionalDataFields } from './components';

import { onlyDigits } from 'src/helpers/string';
import { removeSpacesAndSpecialChars } from 'src/helpers/removeSpacesAndSpecialChars';
import useLoading from 'src/helpers/useLoading';
import { DateFromFormat, DateFromISOToBrazilianFormat, validateDateFromBrazilianForm } from 'src/helpers/Date';
import { validateCPF } from 'src/helpers/cpf';
import { STATES } from 'src/helpers/brazilStates';

import { useFeedbackContext } from 'src/providers/FeedbackProvider';
import { Tracker } from 'src/shared/tracker/Tracker';
import { publicApi } from 'src/publicApi';
import { SignUpService } from 'src/services/signUp';
import { isMobileApp } from 'src/helpers/isMobileApp';
import {
  trackNewSignUpProfessionalDataView,
  trackNewSignUpFinishClick,
} from 'src/services/tracking/events/signup.events';

interface CreateUserPayload {
  email: string;
  password: string;
  cellphoneNumber: string;
  document: string;
  fullName: string;
  birthDate: string;
  createdThroughApp: boolean;
  createdThroughB2B?: boolean;
  professionalRecord: {
    issuer: string;
    number: string;
    uf: string;
  };
}

interface AutocompleteData {
  document?: string;
  fullName?: string;
  birthDate?: string;
  professionalRecordIssuer?: 'CRM' | 'CRO';
  professionalRecordIssuerState?: string;
  professionalRecord?: string;
  phoneNumber?: string;
}

interface ProfessionalAndPersonalDataProps {
  accessData: AccessDataValues;
  onGoBack: () => void;
  onDataChange: (data: ProfessionalDataValues) => void;
  isFirstStepCompleted: boolean;
  initialData: ProfessionalDataValues | null;
  autocompleteData?: AutocompleteData | null;
  isLoadingAutocomplete?: boolean;
  hasToken?: boolean;
  onShowHelp?: () => void;
}

export function ProfessionalAndPersonalData({
  accessData,
  onGoBack,
  onDataChange,
  isFirstStepCompleted,
  initialData,
  autocompleteData,
  isLoadingAutocomplete,
  hasToken = false,
  onShowHelp,
}: ProfessionalAndPersonalDataProps) {
  const navigate = useNavigate();

  const { openToast } = useFeedbackContext();
  const [loading, runWithLoading] = useLoading();
  const [localAutocompleteData, setLocalAutocompleteData] = useState<AutocompleteData | null>(null);
  const [isLoadingLocalAutocomplete, setIsLoadingLocalAutocomplete] = useState(false);
  const [isValidatingProfessionalRecord, setIsValidatingProfessionalRecord] = useState(false);

  const finalAutocompleteData = autocompleteData || localAutocompleteData;

  useEffect(() => {
    trackNewSignUpProfessionalDataView();
  }, []);

  const documentFromAutocomplete = finalAutocompleteData?.document || '';
  const nameFromAutocomplete = finalAutocompleteData?.fullName || '';
  const birthDateFromAutocomplete = useMemo(() => {
    if (!finalAutocompleteData?.birthDate) return '';

    try {
      return DateFromISOToBrazilianFormat(finalAutocompleteData.birthDate);
    } catch (error) {
      console.error('Erro ao formatar data de nascimento:', error);
      return '';
    }
  }, [finalAutocompleteData?.birthDate]);

  const counselFromAutocomplete = useMemo(() => {
    const issuer = finalAutocompleteData?.professionalRecordIssuer;
    if (issuer === 'CRM' || issuer === 'CRO') {
      return issuer;
    }
    return 'CRM';
  }, [finalAutocompleteData?.professionalRecordIssuer]);

  const ufFromAutocomplete = useMemo(() => {
    const state = finalAutocompleteData?.professionalRecordIssuerState;
    if (!state) return '';
    const validState = STATES.find((s) => s.initials === state.toUpperCase());
    return validState ? validState.initials : '';
  }, [finalAutocompleteData?.professionalRecordIssuerState]);

  const registerNumberFromAutocomplete = finalAutocompleteData?.professionalRecord || '';

  const formInitialValues: ProfessionalDataValues = useMemo(
    () =>
      initialData || {
        document: documentFromAutocomplete,
        fullName: nameFromAutocomplete,
        birthDate: birthDateFromAutocomplete,
        counsel: counselFromAutocomplete,
        UFRegister: ufFromAutocomplete,
        registerNumber: registerNumberFromAutocomplete,
      },
    [
      initialData,
      documentFromAutocomplete,
      nameFromAutocomplete,
      birthDateFromAutocomplete,
      counselFromAutocomplete,
      ufFromAutocomplete,
      registerNumberFromAutocomplete,
    ]
  );

  const formik = useFormik<ProfessionalDataValues>({
    initialValues: formInitialValues,
    validationSchema: ProfessionalAndPersonalDataSchema,
    validateOnChange: false,
    validateOnBlur: false,
    enableReinitialize: false,
    onSubmit: async (values) => {
      runWithLoading(async () => {
        try {
          const birthDateIso = DateFromFormat(values.birthDate || '', 'dd/MM/yyyy');

          const validationResponse = await SignUpService.validateProfessionalRecord({
            number: values.registerNumber,
            uf: values.UFRegister,
            issuer: values.counsel,
            birthDate: birthDateIso,
            document: onlyDigits(values.document),
          });

          const validationData = validationResponse.data;

          if (!validationData.isValid) {
            let errorMessage = 'CRM/UF inválidos';

            if (validationData.reason === 'ALREADY_REGISTERED') {
              errorMessage = 'N° REGISTRO JÁ CADASTRADO. RECUPERAR ACESSO';
            } else if (validationData.reason === 'INVALID_CRM') {
              errorMessage = 'N° REGISTRO INVÁLIDO.';
            }

            formik.setFieldError('registerNumber', errorMessage);

            Tracker.trackEvent({
              event: 'erroCadastroPrescritorDadosProfissionais',
              parameters: { error: validationData.reason },
            });

            return;
          }

          trackNewSignUpFinishClick();

          const createUserPayload: CreateUserPayload = {
            email: accessData.email,
            password: accessData.password,
            cellphoneNumber: onlyDigits(accessData.cellphoneNumber),
            document: onlyDigits(values.document),
            fullName: removeSpacesAndSpecialChars(values.fullName),
            birthDate: birthDateIso,
            createdThroughApp: isMobileApp(),
            createdThroughB2B: hasToken,
            professionalRecord: {
              issuer: values.counsel,
              number: values.registerNumber,
              uf: values.UFRegister,
            },
          };

          await publicApi.post('/sign-up/user/create', createUserPayload);

          Tracker.trackEvent({
            event: 'Cadastro Realizado',
            parameters: {
              Nome: createUserPayload.fullName,
              email: createUserPayload.email,
              'Data cadastro': new Date(),
            },
          });

          navigate({
            pathname: '/confirmaremail',
            search: createSearchParams({
              email: accessData.email,
            }).toString(),
          });

          sessionStorage.removeItem('personal-data-form');
          sessionStorage.removeItem('professional-data-form');
          sessionStorage.removeItem('place-service-form');
        } catch (e) {
          if (isAxiosError(e)) {
            const errorMessage = e.response?.data?.message || 'Erro ao criar conta';

            Tracker.trackEvent({
              event: 'erroCadastroPrescritorDadosProfissionais',
              parameters: { error: errorMessage },
            });

            openToast(errorMessage, { type: 'error-outline' });
            return;
          }

          openToast('Ops.. Um problema inesperado ocorreu. Aguarde alguns minutos e tente novamente.', {
            type: 'error',
          });
        }
      });
    },
  });

  useEffect(() => {
    const dataToUse = !isLoadingAutocomplete && !isLoadingLocalAutocomplete ? finalAutocompleteData : null;

    if (dataToUse) {
      if (!formik.values.document && documentFromAutocomplete) {
        formik.setFieldValue('document', documentFromAutocomplete);
      }
      if (!formik.values.fullName && nameFromAutocomplete) {
        formik.setFieldValue('fullName', nameFromAutocomplete);
      }
      if (!formik.values.birthDate && birthDateFromAutocomplete) {
        formik.setFieldValue('birthDate', birthDateFromAutocomplete);
      }
      if (counselFromAutocomplete && formik.values.counsel !== counselFromAutocomplete) {
        formik.setFieldValue('counsel', counselFromAutocomplete);
      }
      if (!formik.values.UFRegister && ufFromAutocomplete) {
        formik.setFieldValue('UFRegister', ufFromAutocomplete);
      }
      if (!formik.values.registerNumber && registerNumberFromAutocomplete) {
        formik.setFieldValue('registerNumber', registerNumberFromAutocomplete);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingAutocomplete,
    isLoadingLocalAutocomplete,
    finalAutocompleteData,
    documentFromAutocomplete,
    nameFromAutocomplete,
    birthDateFromAutocomplete,
    counselFromAutocomplete,
    ufFromAutocomplete,
    registerNumberFromAutocomplete,
  ]);

  useEffect(() => {
    const data: ProfessionalDataValues = {
      document: formik.values.document,
      fullName: formik.values.fullName,
      birthDate: formik.values.birthDate,
      counsel: formik.values.counsel,
      UFRegister: formik.values.UFRegister,
      registerNumber: formik.values.registerNumber,
    };
    onDataChange(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.document,
    formik.values.fullName,
    formik.values.birthDate,
    formik.values.counsel,
    formik.values.UFRegister,
    formik.values.registerNumber,
  ]);

  return (
    <form
      name="professional-and-personal-data"
      onSubmit={(e) => {
        e.preventDefault();
        formik.handleSubmit(e);
      }}
      style={{ width: '100%' }}
    >
      {(() => {
        const formikProps = formik;

        const validatePersonData = async (document: string, birthDate: string) => {
          if (!document || !birthDate || hasToken) return;

          const isDocumentValid = document && validateCPF(document);
          const isBirthDateValid = birthDate && validateDateFromBrazilianForm(birthDate);

          if (!isDocumentValid || !isBirthDateValid) return;

          setIsLoadingLocalAutocomplete(true);
          formikProps.setFieldError('document', '');

          try {
            const [day, month, year] = birthDate.split('/');
            const birthDateFormatted = `${year}-${month}-${day}`;

            const response = await SignUpService.autocomplete({
              document: onlyDigits(document),
              birthDate: birthDateFormatted,
            });

            if (response.data) {
              setLocalAutocompleteData(response.data);
            }
          } catch (error) {
            if (isAxiosError(error) && error.response?.status !== 502) {
              formikProps.setFieldError('document', 'CPF e data de nascimento não correspondem');
            }
          } finally {
            setIsLoadingLocalAutocomplete(false);
          }
        };

        const validateProfessionalRecord = async (
          registerNumber: string,
          uf: string,
          issuer: string,
          document: string,
          birthDate: string
        ) => {
          if (!registerNumber || !uf || !issuer || !document || !birthDate) return;

          setIsValidatingProfessionalRecord(true);
          formikProps.setFieldError('registerNumber', '');

          try {
            const birthDateIso = DateFromFormat(birthDate || '', 'dd/MM/yyyy');

            const validationResponse = await SignUpService.validateProfessionalRecord({
              number: registerNumber,
              uf: uf,
              issuer: issuer,
              birthDate: birthDateIso,
              document: onlyDigits(document),
            });

            const validationData = validationResponse.data;

            if (!validationData.isValid) {
              // Define a mensagem baseada no reason
              let errorMessage = 'CRM/UF inválidos';

              if (validationData.reason === 'ALREADY_REGISTERED') {
                errorMessage = 'N° REGISTRO JÁ CADASTRADO. RECUPERAR ACESSO';
              } else if (validationData.reason === 'INVALID_CRM') {
                errorMessage = 'N° REGISTRO INVÁLIDO.';
              }

              formikProps.setFieldError('registerNumber', errorMessage);

              Tracker.trackEvent({
                event: 'erroCadastroPrescritorDadosProfissionais',
                parameters: { error: validationData.reason },
              });
            } else {
              formikProps.setFieldError('registerNumber', '');
            }
          } catch (error) {
            formikProps.setFieldError('registerNumber', 'CRM/UF inválidos');
          } finally {
            setIsValidatingProfessionalRecord(false);
          }
        };

        return (
          <>
            <NewTabsHeader
              currentIndex={NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA}
              isFirstStepCompleted={isFirstStepCompleted}
              onTabClick={(index) => {
                if (index === NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA) return;
                onGoBack();
              }}
            />

            <Box>
              <PersonalDataFields
                document={formikProps.values.document}
                fullName={formikProps.values.fullName}
                birthDate={formikProps.values.birthDate}
                documentError={formikProps.errors.document}
                fullNameError={formikProps.errors.fullName}
                birthDateError={formikProps.errors.birthDate}
                onDocumentChange={(e) => {
                  formikProps.handleChange(e);
                  const documentValue = e.target.value;
                  const documentDigits = onlyDigits(documentValue);

                  if (documentDigits.length === 11) {
                    setTimeout(() => {
                      validatePersonData(documentValue, formikProps.values.birthDate);
                    }, 100);
                  }
                }}
                onFullNameChange={formikProps.handleChange}
                onBirthDateChange={(e) => {
                  formikProps.handleChange(e);
                  const birthDateValue = e.target.value;

                  if (birthDateValue.length === 10) {
                    setTimeout(() => {
                      validatePersonData(formikProps.values.document, birthDateValue);
                    }, 100);
                  }
                }}
                onDocumentBlur={() => {
                  formikProps.validateField('document');
                  validatePersonData(formikProps.values.document, formikProps.values.birthDate);
                }}
                onFullNameBlur={() => formikProps.validateField('fullName')}
                onBirthDateBlur={() => {
                  formikProps.validateField('birthDate');
                  validatePersonData(formikProps.values.document, formikProps.values.birthDate);
                }}
                isFullNameLoading={isLoadingLocalAutocomplete}
              />

              <ProfessionalDataFields
                counsel={formikProps.values.counsel}
                ufRegister={formikProps.values.UFRegister}
                registerNumber={formikProps.values.registerNumber}
                counselError={formikProps.errors.counsel}
                ufRegisterError={formikProps.errors.UFRegister}
                registerNumberError={formikProps.errors.registerNumber}
                onCounselChange={(counsel) => formikProps.setFieldValue('counsel', counsel)}
                onUfRegisterChange={(value) => formikProps.setFieldValue('UFRegister', value)}
                onRegisterNumberChange={formikProps.handleChange}
                onUfRegisterBlur={() => formikProps.validateField('UFRegister')}
                onRegisterNumberBlur={() => {
                  formikProps.validateField('registerNumber');
                  validateProfessionalRecord(
                    formikProps.values.registerNumber,
                    formikProps.values.UFRegister,
                    formikProps.values.counsel,
                    formikProps.values.document,
                    formikProps.values.birthDate
                  );
                }}
                isRegisterNumberLoading={isValidatingProfessionalRecord}
                onShowHelp={onShowHelp}
              />

              <NewNextButton
                currentIndex={NEW_TABS_INDEX.PROFESSIONAL_AND_PERSONAL_DATA}
                position={{ base: 'fixed', sm: 'static' }}
                padding={{ base: '0 20px', sm: '0' }}
                buttonText="Finalizar cadastro"
                isLoading={
                  loading || isLoadingAutocomplete || isLoadingLocalAutocomplete || isValidatingProfessionalRecord
                }
              />
            </Box>
          </>
        );
      })()}
    </form>
  );
}
